import T from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as Sentry from '@sentry/browser';
import { MediaFile, Show } from 'resources';
import Episode, { encodeAudio } from 'resources/episode';
import { fetchShowFeatureFlags } from 'ducks/feature-flags';
import resourceProp from 'shared/helpers/resource-prop';
import Modal from 'shared/components/modal';
import { getAudioMarker, getAudioMarkerUrl } from 'shared/helpers/markers';
import Navigation from './navigation';
import { ensureAdMarkersExists, fetchEpisodeMarkers, selectMarkers } from '../../ducks/markers';

class EpisodePage extends Component {
  static propTypes = {
    children: T.node,
    episode: resourceProp(Episode),
    markers: T.array,
    mediaFile: resourceProp(MediaFile),
    onEnsureAdMarkersExists: T.func.isRequired,
    onFetchEpisodeMarkers: T.func.isRequired,
    onFetchShowFeatureFlags: T.func.isRequired,
    params: T.shape({
      episodeId: T.string.isRequired,
    }).isRequired,
    readMediaFile: T.func,
    show: resourceProp(Show),
  };

  static childContextTypes = {
    episode: resourceProp(Episode),
    hasMediaFileError: T.bool,
    markers: T.array,
    mediaFile: resourceProp(MediaFile),
    retryEncoder: T.func,
    show: resourceProp(Show),
  };

  getChildContext() {
    const { show, episode, markers, mediaFile } = this.props;
    const hasMediaFileError = Boolean(mediaFile.error);
    const retryEncoder = this.retryAudioEncoder.bind(this);

    return {
      show,
      episode,
      markers,
      mediaFile,
      hasMediaFileError,
      retryEncoder,
    };
  }

  componentDidMount() {
    const { params, onFetchEpisodeMarkers } = this.props;

    this.handleUpdate();
    onFetchEpisodeMarkers(params.episodeId);
  }

  componentDidUpdate() {
    this.handleUpdate();
    this.handleFeatureFlagFetch();
  }

  getAudioMarkerAsAudioMarker() {
    const { markers } = this.props;
    const hasMarkers = markers && markers.length > 0;
    const audioMarker = hasMarkers && getAudioMarker(markers);
    return audioMarker;
  }

  handleFeatureFlagFetch() {
    const { data } = this.props.episode;
    const { onFetchShowFeatureFlags } = this.props;
    if (data) {
      onFetchShowFeatureFlags(data.show);
    }
  }

  handleUpdate() {
    this.maybeEncodeAudio();
    this.maybeHandleEncodingCompleted();
  }

  maybeEncodeAudio() {
    if (this.shouldEncode()) {
      this.cachedEncodeAudio();
    }
  }

  shouldEncode() {
    if (this.props.mediaFile.pending) {
      return false;
    }

    const mediaFileData = this.props.mediaFile.data;
    const episodeData = this.props.episode.data;
    const mediaFileUrl = mediaFileData && mediaFileData.sourceMediaUrl;
    const audioMarkerUrl = getAudioMarkerUrl(this.props.markers);

    return Boolean(episodeData && audioMarkerUrl && audioMarkerUrl !== mediaFileUrl);
  }

  cachedEncodeAudio(force = false) {
    if (!this.encodingAudio) {
      const resetEncodingAudio = () => {
        this.encodingAudio = undefined;
      };

      const mediaUrl = getAudioMarkerUrl(this.props.markers);

      this.encodingAudio = encodeAudio(this.props.episode.data.id, mediaUrl, force)
        .then(resetEncodingAudio)
        .catch(error => {
          Sentry.captureException(error, {
            tags: {
              // eslint-disable-next-line camelcase
              failed_encoding: true,
            },
          });
          resetEncodingAudio();
        });
    }
  }

  maybeHandleEncodingCompleted() {
    if (this.hasEncodedFile()) {
      this.cachedHandleEncodingCompleted();
    }
  }

  hasEncodedFile() {
    const audioMarker = getAudioMarker(this.props.markers);
    const mediaFileData = this.props.mediaFile.data;
    const episodeData = this.props.episode.data;
    const showData = this.props.show.data;

    return Boolean(
      audioMarker &&
        mediaFileData &&
        episodeData &&
        showData &&
        getAudioMarkerUrl(this.props.markers) &&
        getAudioMarkerUrl(this.props.markers) === mediaFileData.sourceMediaUrl
    );
  }

  presentErrorModal(error) {
    Sentry.captureMessage('Failed removing ad markers', { level: 'info' });
    Sentry.captureException(error);

    return Modal.present({
      title: 'Something went wrong.',
      dialog:
        'Please refresh the page. If the problem persists, please contact the support. Thank you!',
      actions: [{ name: 'Refresh', value: true }],
    }).then(() => {
      window.location.reload(false);
    });
  }

  cachedHandleEncodingCompleted() {
    const { episode, mediaFile, onEnsureAdMarkersExists } = this.props;

    if (!this.handlingEncodingCompleted) {
      const resetHandlingEncodingCompleted = () => {
        this.handlingEncodingCompleted = undefined;
      };

      this.handlingEncodingCompleted = onEnsureAdMarkersExists(
        episode.data.id,
        mediaFile.data.duration
      )
        .catch(this.presentErrorModal)
        .then(resetHandlingEncodingCompleted)
        .catch(resetHandlingEncodingCompleted);
    }
  }

  retryAudioEncoder() {
    if (this.props.mediaFile.pending) {
      return;
    }
    this.cachedEncodeAudio(true);
  }

  render() {
    const { children, episode, markers } = this.props;

    if (!episode || !episode.data || !markers) {
      return null;
    }

    return (
      <div className="episode-page">
        <Navigation episode={episode} />

        {children}
      </div>
    );
  }
}

const withShow = Show.connect('show', 'episode.data.show');
const withEpisode = Episode.connect('episode', 'params.episodeId');
const withMediaFile = MediaFile.connect('mediaFile', 'params.episodeId');

export default connect(
  (state, { params }) => ({
    markers: selectMarkers(state, params.episodeId),
  }),
  {
    onFetchEpisodeMarkers: fetchEpisodeMarkers,
    onEnsureAdMarkersExists: ensureAdMarkersExists,
    onFetchShowFeatureFlags: fetchShowFeatureFlags,
  }
)(withMediaFile(withEpisode(withShow(EpisodePage))));
